import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  RiCalendarLine,
  RiCheckLine,
  RiCloseLine,
  RiTimeLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiTeamLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiCalendarCheckLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiHourglassLine,
  RiMapPinLine,
  RiEyeLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BASE_URL = `${process.env.REACT_APP_API_URL}/admin`;
const ATTENDANCE_URL = `${process.env.REACT_APP_API_URL}/attendance`;

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div
      className={`fixed bottom-6 right-6 z-[9999] flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : type === "warning"
            ? "bg-amber-50 border border-amber-200 text-amber-800"
            : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : type === "warning" ? (
        <RiHourglassLine className="w-4 h-4 text-amber-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-5 h-5" />
      </button>
    </div>
  );
};

const STATUS_CONFIG = {
  present: {
    label: "Present",
    badgeColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
    dotColor: "bg-emerald-500",
  },
  pending: {
    label: "Pending",
    badgeColor: "bg-amber-50 text-amber-700 border-amber-200",
    dotColor: "bg-amber-400",
  },
  absent: {
    label: "Absent",
    badgeColor: "bg-red-50 text-red-600 border-red-200",
    dotColor: "bg-red-500",
  },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG["absent"];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.badgeColor}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotColor}`} />
      {cfg.label}
    </span>
  );
};

/* ─── Review Modal ───────────────────────────────────────── */
// Now handles ALL pending entries, not just the first one
const ReviewModal = ({
  member,
  attendanceData,
  onClose,
  onApprove,
  onReject,
  saving,
}) => {
  const pendingEntries =
    attendanceData?.entries?.filter((e) => e.approvalStatus === "pending") ||
    [];

  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    setCurrentIdx(0);
  }, [attendanceData]);

  if (!attendanceData || pendingEntries.length === 0) return null;

  const pendingEntry = pendingEntries[currentIdx];
  const total = pendingEntries.length;

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "--";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const LocationBlock = ({ location, color = "emerald", label }) => {
    if (!location)
      return (
        <p className="text-[11px] text-gray-400 px-4 pb-3">No location data</p>
      );

    const colorMap = {
      emerald: {
        bg: "bg-emerald-50",
        border: "border-emerald-100",
        text: "text-emerald-700",
        link: "text-emerald-600 hover:text-emerald-700",
      },
      rose: {
        bg: "bg-rose-50",
        border: "border-rose-100",
        text: "text-rose-700",
        link: "text-rose-600 hover:text-rose-700",
      },
    };
    const c = colorMap[color];
    const lat = location.coordinates?.[1];
    const lng = location.coordinates?.[0];

    return (
      <div className={`px-4 pb-4 bg-white space-y-2`}>
        <p
          className={`text-md font-semibold ${c.text} flex items-center gap-1`}
        >
          <RiMapPinLine size={18} />
          {label}
        </p>
        <div
          className={`p-3 ${c.bg} rounded-xl ${c.border} border space-y-1.5`}
        >
          <p className="text-xs text-gray-700 break-words leading-relaxed">
            {location.address || "Address unavailable"}
          </p>
          {/* {lat != null && lng != null && (
            <p className="text-[10px] text-gray-400">
              {lat.toFixed(6)}, {lng.toFixed(6)}
              {location.accuracy &&
                ` · ±${Math.round(location.accuracy)}m accuracy`}
            </p>
          )} */}
          {lat != null && lng != null && (
            <a
              href={`https://www.google.com/maps?q=${lat},${lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1 text-xl font-semibold ${c.link} underline underline-offset-2`}
            >
              <RiMapPinLine size={22} />
              Open in Google Maps
            </a>
          )}
        </div>
      </div>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-blue-950/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-2xl mx-auto bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-amber-100 bg-amber-50 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center text-white shadow-sm">
              <RiHourglassLine className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-amber-900">
                Review Attendance Request
              </h2>
              <p className="text-[11px] text-amber-500">
                Verify details before approving or rejecting
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Entry navigator — only when multiple pending entries */}
            {total > 1 && (
              <div className="flex items-center gap-1 bg-amber-100 border border-amber-200 rounded-lg px-2 py-1">
                <button
                  onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
                  disabled={currentIdx === 0}
                  className="text-amber-600 disabled:opacity-30 hover:text-amber-800 transition"
                >
                  <RiArrowLeftSLine className="w-4 h-4" />
                </button>
                <span className="text-[11px] font-semibold text-amber-700 px-1">
                  {currentIdx + 1} / {total}
                </span>
                <button
                  onClick={() =>
                    setCurrentIdx((i) => Math.min(total - 1, i + 1))
                  }
                  disabled={currentIdx === total - 1}
                  className="text-amber-600 disabled:opacity-30 hover:text-amber-800 transition"
                >
                  <RiArrowRightSLine className="w-4 h-4" />
                </button>
              </div>
            )}
            <button
              onClick={onClose}
              className="text-amber-400 hover:text-amber-700 transition rounded-lg p-1 hover:bg-amber-100"
            >
              <RiCloseLine className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Staff Info */}
          <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100">
            {member?.profilePic ? (
              <img
                src={member.profilePic}
                alt={member.name}
                className="w-14 h-14 rounded-full object-cover border-2 border-white shadow-md"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white text-xl font-bold shadow-md">
                {(member?.name || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-slate-800 truncate">
                {member?.name}
              </p>
              <p className="text-sm text-slate-500">
                {member?.position || "Staff"}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">{member?.email}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200 shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              Pending
            </span>
          </div>

          {/* Date + entry type */}
          <div className="flex items-center gap-3">
            <div className="flex-1 p-4 rounded-xl bg-gray-50 border border-gray-100">
              <div className="flex items-center gap-2 text-gray-500 mb-1">
                <RiCalendarLine className="w-4 h-4" />
                <span className="text-[11px] font-semibold uppercase tracking-wider">
                  Attendance Date
                </span>
              </div>
              <p className="text-sm font-semibold text-gray-800">
                {new Date(attendanceData.date).toLocaleDateString("en-IN", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
            <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 shrink-0 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">
                Request Type
              </p>
              <span
                className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border ${
                  pendingEntry.status === "checked-in"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-rose-50 text-rose-700 border-rose-100"
                }`}
              >
                {pendingEntry.status === "checked-in"
                  ? "Check-in"
                  : "Check-out"}
              </span>
            </div>
          </div>

          {/* ── Check In block ── */}
          <div className="rounded-xl border border-emerald-100 overflow-hidden">
            {/* Title row */}
            <div className="flex items-center justify-between px-4 py-3 bg-emerald-50">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                  <RiCheckLine className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                  Check In
                </span>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-emerald-700">
                  {pendingEntry.checkInTime}
                </p>
                <p className="text-[10px] text-emerald-500">
                  Submitted: {formatDateTime(pendingEntry.createdAt)}
                </p>
              </div>
            </div>

            {/* Check-in location — always visible in review */}
            <LocationBlock
              location={pendingEntry.checkInLocation}
              color="emerald"
              label="Check-in Location"
            />
          </div>

          {/* ── Check Out block (only if entry has checkout) ── */}
          {pendingEntry.checkOutTime && (
            <div className="rounded-xl border border-rose-100 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-rose-50">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-rose-500 flex items-center justify-center">
                    <RiCloseLine className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-rose-700 uppercase tracking-wider">
                    Check Out
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-rose-700">
                    {pendingEntry.checkOutTime}
                  </p>
                  {pendingEntry.workHours > 0 && (
                    <p className="text-[10px] text-rose-500">
                      {pendingEntry.workHours} hrs worked
                    </p>
                  )}
                </div>
              </div>

              <LocationBlock
                location={pendingEntry.checkOutLocation}
                color="rose"
                label="Check-out Location"
              />
            </div>
          )}

          {/* Notes */}
          {pendingEntry.notes && (
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
              <span className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider block mb-1.5">
                Notes
              </span>
              <p className="text-sm text-gray-700 leading-relaxed">
                {pendingEntry.notes}
              </p>
            </div>
          )}

          {/* Daily Summary */}
          <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Daily Summary
            </p>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-white rounded-xl border border-gray-100">
                <p className="text-2xl font-bold text-blue-600">
                  {attendanceData.dailySummary?.totalCheckIns || 0}
                </p>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mt-0.5">
                  Check-ins
                </p>
              </div>
              <div className="text-center p-3 bg-white rounded-xl border border-gray-100">
                <p className="text-2xl font-bold text-amber-500">
                  {pendingEntries.length}
                </p>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mt-0.5">
                  Pending
                </p>
              </div>
              <div className="text-center p-3 bg-white rounded-xl border border-gray-100">
                <p className="text-2xl font-bold text-emerald-600">
                  {attendanceData.dailySummary?.totalWorkHours || 0}h
                </p>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mt-0.5">
                  Work Hrs
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-100 transition disabled:opacity-50"
          >
            <RiCloseLine className="w-4 h-4" />
            Cancel
          </button>
          <button
            onClick={() => onReject(attendanceData._id, pendingEntry._id)}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 transition disabled:opacity-50"
          >
            {saving ? (
              <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
            ) : (
              <RiCloseCircleLine className="w-4 h-4" />
            )}
            Reject
          </button>
          <button
            onClick={() => onApprove(attendanceData._id, pendingEntry._id)}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-200 transition disabled:opacity-50"
          >
            {saving ? (
              <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
            ) : (
              <RiCheckboxCircleLine className="w-4 h-4" />
            )}
            Approve
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const ManualAttendanceModal = ({ member, date, onClose, onSubmit, saving }) => {
  const [checkInTime, setCheckInTime] = useState("09:00");
  const [addCheckOut, setAddCheckOut] = useState(false);
  const [checkOutTime, setCheckOutTime] = useState("18:00");

  const handleSubmit = () => {
    onSubmit({
      checkInTime,
      checkOutTime: addCheckOut ? checkOutTime : undefined,
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-md mx-auto bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white">
              <RiCalendarCheckLine className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-blue-900">
                Mark as Present
              </h2>
              <p className="text-[11px] text-blue-400">
                {member?.name} • {formatDateDisplay(date)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">
              Check-in Time
            </label>
            <input
              type="time"
              value={checkInTime}
              onChange={(e) => setCheckInTime(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={addCheckOut}
              onChange={(e) => setAddCheckOut(e.target.checked)}
              className="rounded border-gray-300"
            />
            Also add check-out time
          </label>

          {addCheckOut && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5">
                Check-out Time
              </label>
              <input
                type="time"
                value={checkOutTime}
                onChange={(e) => setCheckOutTime(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          )}

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex gap-3">
              <RiErrorWarningLine className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  Important Notice
                </p>
                <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                  This action will manually mark the employee as present for the
                  selected date. Once recorded, this attendance entry cannot be
                  automatically reversed or undone. Please verify the details
                  before proceeding.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-100 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-200 transition disabled:opacity-50"
          >
            {saving ? (
              <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
            ) : (
              <RiCheckboxCircleLine className="w-4 h-4" />
            )}
            Mark Present
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const EntryDetailsModal = ({ staffId, member, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [idx, setIdx] = useState(0);
  const [showCheckInLocation, setShowCheckInLocation] = useState(false);
  const [showCheckOutLocation, setShowCheckOutLocation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);

  useEffect(() => {
    fetchStaffAttendance();
  }, [member._id]);

  console.log("staffId", member._id);

  const fetchStaffAttendance = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${ATTENDANCE_URL}/staff/${member._id}`);
      const data = res.data?.data || res.data || [];
      setAttendanceRecords(data);
      console.log("attendanceRecords", attendanceRecords);

      const allEntries = data.flatMap((record) =>
        record.entries.map((entry) => ({
          ...entry,
          date: record.date,
          dailySummary: record.dailySummary,
        })),
      );
      setEntries(allEntries);
    } catch (error) {
      console.error("Error fetching staff attendance:", error);
      setError("Failed to load attendance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setShowCheckInLocation(false);
    setShowCheckOutLocation(false);
  }, [idx]);

  const entry = entries[idx];
  const total = entries.length;

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "--";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return createPortal(
      <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative z-10 w-full max-w-md mx-auto bg-white rounded-2xl shadow-2xl p-8 text-center">
          <AiOutlineLoading3Quarters className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-3" />
          <p className="text-gray-600">Loading attendance records...</p>
        </div>
      </div>,
      document.body,
    );
  }

  if (error || entries.length === 0) {
    return createPortal(
      <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="relative z-10 w-full max-w-md mx-auto bg-white rounded-2xl shadow-2xl p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 mx-auto flex items-center justify-center mb-3">
            <RiErrorWarningLine className="w-7 h-7 text-red-400" />
          </div>
          <p className="text-gray-600 mb-4">
            {error || "No attendance records found"}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-10 w-full max-w-2xl mx-auto bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center text-white">
              <RiEyeLine className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-blue-900">
                Attendance History
              </h2>
              <p className="text-[11px] text-blue-400">
                {member?.name || "Staff"} • {total} record
                {total !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {total > 1 && (
              <div className="flex items-center gap-1 bg-blue-100 border border-blue-200 rounded-lg px-2 py-1">
                <button
                  onClick={() => setIdx((i) => Math.max(0, i - 1))}
                  disabled={idx === 0}
                  className="text-blue-500 disabled:opacity-30"
                >
                  <RiArrowLeftSLine className="w-4 h-4" />
                </button>
                <span className="text-[11px] font-semibold text-blue-700 px-1">
                  {idx + 1} / {total}
                </span>
                <button
                  onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
                  disabled={idx === total - 1}
                  className="text-blue-500 disabled:opacity-30"
                >
                  <RiArrowRightSLine className="w-4 h-4" />
                </button>
              </div>
            )}

            <button
              onClick={onClose}
              className="text-blue-400 hover:text-blue-700"
            >
              <RiCloseLine className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Date Header */}
          <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <RiCalendarLine className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-semibold text-slate-700">
                  {formatDate(entry.date)}
                </span>
              </div>
              <span
                className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
                  entry?.approvalStatus === "approved"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : entry?.approvalStatus === "pending"
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-red-50 text-red-600 border-red-200"
                }`}
              >
                {entry?.approvalStatus || "pending"}
              </span>
            </div>
            {entry?.dailySummary?.totalWorkHours > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                Total work hours: {entry.dailySummary.totalWorkHours} hrs
              </p>
            )}
          </div>

          {/* Check In */}
          <div className="rounded-xl border border-emerald-100 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-emerald-50">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                  <RiCheckLine className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                  Check In
                </span>
              </div>
              <span className="text-sm font-bold text-emerald-700">
                {entry?.checkInTime || "--"}
              </span>
            </div>

            {/* Check-in Location */}
            {entry?.checkInLocation ? (
              <div className="px-4 py-3 bg-white border-t border-emerald-100">
                <button
                  onClick={() => setShowCheckInLocation((v) => !v)}
                  className="flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 font-medium transition"
                >
                  <RiMapPinLine className="w-3.5 h-3.5" />
                  {showCheckInLocation
                    ? "Hide Location"
                    : "Show Check-in Location"}
                </button>

                {showCheckInLocation && (
                  <div className="mt-2 p-3 bg-emerald-50 rounded-lg border border-emerald-100 space-y-1.5">
                    <p className="text-xs font-medium text-emerald-700 flex items-center gap-1">
                      <RiMapPinLine className="w-3.5 h-3.5" />
                      Address
                    </p>

                    <p className="text-xs text-gray-700 break-words leading-relaxed">
                      {entry?.checkInLocation?.address || "Address unavailable"}
                    </p>

                    {entry?.checkInLocation?.coordinates?.length === 2 && (
                      <>
                        {/* <p className="text-[10px] text-gray-500">
                          Lat:{" "}
                          {entry?.checkInLocation?.coordinates[1]?.toFixed(6)} •
                          Lng:{" "}
                          {entry?.checkInLocation?.coordinates[0]?.toFixed(6)}
                          {entry?.checkInLocation?.accuracy &&
                            ` • ±${Math.round(entry?.checkInLocation?.accuracy)}m`}
                        </p> */}

                        <a
                          href={`https://www.google.com/maps?q=${entry.checkInLocation.coordinates[1]},${entry.checkInLocation.coordinates[0]}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-md font-semibold text-emerald-600 hover:text-emerald-700 underline underline-offset-2"
                        >
                          <RiMapPinLine size={20} />
                          Open in Google Maps
                        </a>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="px-4 py-3 bg-white border-t border-emerald-100">
                <p className="text-xs text-gray-400">
                  No location data available
                </p>
              </div>
            )}
          </div>

          {/* Check Out */}
          <div className="rounded-xl border border-rose-100 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-rose-50">
              <div className="flex items-center gap-2">
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    entry?.checkOutTime ? "bg-rose-500" : "bg-gray-200"
                  }`}
                >
                  <RiCloseLine
                    className={`w-3.5 h-3.5 ${
                      entry?.checkOutTime ? "text-white" : "text-gray-400"
                    }`}
                  />
                </div>
                <span className="text-xs font-semibold text-rose-700 uppercase tracking-wider">
                  Check Out
                </span>
              </div>
              <span
                className={`text-sm font-bold ${
                  entry?.checkOutTime ? "text-rose-700" : "text-gray-400"
                }`}
              >
                {entry?.checkOutTime || "Not yet"}
              </span>
            </div>

            {entry?.checkOutTime ? (
              <>
                {entry?.workHours > 0 && (
                  <div className="px-4 py-2 bg-white border-t border-rose-100 flex justify-between items-center">
                    <span className="text-xs text-gray-500">Work Hours</span>
                    <span className="text-xs font-bold text-slate-700">
                      {entry?.workHours} hrs
                    </span>
                  </div>
                )}

                {/* Check-out location */}
                {entry?.checkOutLocation ? (
                  <div className="px-4 py-3 bg-white border-t border-rose-100">
                    <button
                      onClick={() => setShowCheckOutLocation((v) => !v)}
                      className="flex items-center gap-1.5 text-xs text-rose-600 hover:text-rose-700 font-medium transition"
                    >
                      <RiMapPinLine className="w-3.5 h-3.5" />
                      {showCheckOutLocation
                        ? "Hide Location"
                        : "Show Check-out Location"}
                    </button>

                    {showCheckOutLocation && (
                      <div className="mt-2 p-3 bg-rose-50 rounded-lg border border-rose-100 space-y-1.5">
                        <p className="text-xs font-medium text-rose-700 flex items-center gap-1">
                          <RiMapPinLine className="w-3.5 h-3.5" />
                          Address
                        </p>

                        <p className="text-xs text-gray-700 break-words leading-relaxed">
                          {entry?.checkOutLocation?.address ||
                            "Address unavailable"}
                        </p>

                        {entry?.checkOutLocation?.coordinates?.length === 2 && (
                          <>
                            {/* <p className="text-[10px] text-gray-500">
                              Lat:{" "}
                              {entry?.checkOutLocation?.coordinates[1]?.toFixed(
                                6,
                              )}{" "}
                              • Lng:{" "}
                              {entry?.checkOutLocation?.coordinates[0]?.toFixed(
                                6,
                              )}
                              {entry?.checkOutLocation?.accuracy &&
                                ` • ±${Math.round(entry?.checkOutLocation?.accuracy)}m`}
                            </p> */}

                            <a
                              href={`https://www.google.com/maps?q=${entry.checkOutLocation.coordinates[1]},${entry.checkOutLocation.coordinates[0]}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-md font-semibold text-rose-600 hover:text-rose-700 underline underline-offset-2"
                            >
                              <RiMapPinLine size={20} />
                              Open in Google Maps
                            </a>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="px-4 py-3 bg-white border-t border-rose-100">
                    <p className="text-xs text-gray-400">
                      No location data available
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="px-4 py-3 bg-white border-t border-rose-100">
                <p className="text-xs text-gray-500">
                  Staff hasn't checked out yet
                </p>
              </div>
            )}
          </div>

          {/* Notes */}
          {entry?.notes && (
            <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
              <span className="text-xs font-semibold text-blue-600 uppercase tracking-wider block mb-1">
                Notes
              </span>
              <p className="text-xs text-gray-600">{entry.notes}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-[10px] text-gray-400 space-y-1">
            <p>Created: {formatDateTime(entry?.createdAt)}</p>
            {entry?.approvedAt && (
              <p>Approved: {formatDateTime(entry?.approvedAt)}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-blue-50 shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
};

const shiftDate = (dateStr, delta) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d + delta);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate(),
  ).padStart(2, "0")}`;
};

/* ─── Main Component ─────────────────────────────────────── */
export default function StaffAttendance() {
  const [staffList, setStaffList] = useState([]);
  const [attendanceData, setAttendanceData] = useState({});
  const [date, setDate] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [detailsEntry, setDetailsEntry] = useState(null);
  const [toast, setToast] = useState(null);
  const [manualEntryMember, setManualEntryMember] = useState(null);
  const [revertMember, setRevertMember] = useState(null);

  const notify = (message, type = "success") => setToast({ message, type });

  const getAdminId = () => {
    const raw = localStorage.getItem("Nexgen-Admin-Data");
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        return parsed?._id || parsed?.id || parsed;
      } catch {
        return raw;
      }
    }
    return null;
  };

  const fetchStaff = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/all-users`);
      const all = res.data?.data || res.data || [];
      return all.filter((u) => u.userType === "staff");
    } catch {
      notify("Failed to fetch staff", "error");
      return [];
    }
  };

  const fetchAttendanceForDate = async (selectedDate) => {
    try {
      const res = await axios.get(
        `${ATTENDANCE_URL}/all-staff/${selectedDate}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching attendance:", error);
      return null;
    }
  };

  // FIX: Use the dedicated daily endpoint instead of filtering all records
  const fetchStaffAttendanceForReview = async (staffId) => {
    try {
      const res = await axios.get(
        `${ATTENDANCE_URL}/staff/${staffId}/daily/${date}`,
      );
      return res.data?.attendance || res.data;
    } catch (error) {
      console.error("Error fetching staff attendance:", error);
      notify("Failed to load attendance details", "error");
      return null;
    }
  };

  const handleRevertToAbsent = async () => {
    if (!revertMember) return;
    setSaving(true);
    try {
      await axios.delete(
        `${ATTENDANCE_URL}/staff/${revertMember._id}/date/${date}`,
      );
      notify(
        `${revertMember.name}'s attendance for ${formatDateDisplay(date)} has been reverted to Absent`,
        "warning",
      );
      setRevertMember(null);
      await loadAll(date);
    } catch (error) {
      notify(
        error.response?.data?.message || "Failed to revert attendance",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const loadAll = async (selectedDate) => {
    setLoading(true);
    const staff = await fetchStaff();
    setStaffList(staff);

    const attendanceRes = await fetchAttendanceForDate(selectedDate);
    if (attendanceRes?.staffList) {
      const map = {};
      attendanceRes.staffList.forEach((item) => {
        map[item.staffId] = item.attendance;
      });
      setAttendanceData(map);
    } else {
      setAttendanceData({});
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAll(date);
  }, [date]);

  const handleApprove = async (attendanceId, entryId) => {
    if (!reviewData) return;
    setSaving(true);
    const adminId = getAdminId();
    try {
      await axios.patch(
        `${ATTENDANCE_URL}/approve/${attendanceId}/entry/${entryId}`,
        { approvedBy: adminId },
      );
      notify(
        `${reviewData.member.name}'s attendance has been approved`,
        "success",
      );
      // Refresh the modal data so admin can action remaining pending entries
      const updated = await fetchStaffAttendanceForReview(
        reviewData.member._id,
      );
      if (
        updated &&
        updated.entries?.some((e) => e.approvalStatus === "pending")
      ) {
        setReviewData((prev) => ({ ...prev, attendanceData: updated }));
      } else {
        setReviewData(null);
      }
      await loadAll(date);
    } catch (error) {
      notify(error.response?.data?.message || "Failed to approve", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async (attendanceId, entryId) => {
    if (!reviewData) return;
    setSaving(true);
    try {
      await axios.patch(
        `${ATTENDANCE_URL}/reject/${attendanceId}/entry/${entryId}`,
      );
      notify(
        `${reviewData.member.name}'s attendance has been rejected`,
        "warning",
      );
      const updated = await fetchStaffAttendanceForReview(
        reviewData.member._id,
      );
      if (
        updated &&
        updated.entries?.some((e) => e.approvalStatus === "pending")
      ) {
        setReviewData((prev) => ({ ...prev, attendanceData: updated }));
      } else {
        setReviewData(null);
      }
      await loadAll(date);
    } catch (error) {
      notify(error.response?.data?.message || "Failed to reject", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleManualMark = async ({ checkInTime, checkOutTime, notes }) => {
    if (!manualEntryMember) return;
    setSaving(true);
    const adminId = getAdminId();
    try {
      await axios.post(`${ATTENDANCE_URL}/mark-manual`, {
        staffId: manualEntryMember._id,
        date,
        checkInTime,
        checkOutTime,
        markedBy: adminId,
        notes,
      });
      notify(
        `${manualEntryMember.name} marked present for ${formatDateDisplay(date)}`,
        "success",
      );
      setManualEntryMember(null);
      await loadAll(date);
    } catch (error) {
      notify(
        error.response?.data?.message || "Failed to mark attendance",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleReviewClick = async (member) => {
    setLoading(true);
    const attendanceRecord = await fetchStaffAttendanceForReview(member._id);
    if (
      attendanceRecord &&
      attendanceRecord.entries?.some((e) => e.approvalStatus === "pending")
    ) {
      setReviewData({ member, attendanceData: attendanceRecord });
    } else {
      notify("No pending entries found for this staff member", "warning");
    }
    setLoading(false);
  };

  const isToday = date === todayStr();

  /* ─── FIX: Correct status derivation ─────────────────────
     partial = someone is checked-in (may or may not be approved)
     completed = at least one approved+checked-out entry exists
     We need to look at entries directly for the admin view.
  ────────────────────────────────────────────────────────── */
  const getDisplayStatus = (attendance) => {
    const entries = attendance?.entries || [];
    if (entries.length === 0) return "absent";

    // Any open approved check-in → "present" (actively working)
    const hasActiveApproved = entries.some(
      (e) => e.status === "checked-in" && e.approvalStatus === "approved",
    );
    if (hasActiveApproved) return "present";

    // Any pending entry → "pending"
    const hasPending = entries.some((e) => e.approvalStatus === "pending");
    if (hasPending) return "pending";

    // At least one approved completed session → "present"
    const hasApprovedComplete = entries.some(
      (e) => e.approvalStatus === "approved" && e.status === "checked-out",
    );
    if (hasApprovedComplete) return "present";

    return "absent";
  };

  const hasPendingEntry = (attendance) =>
    attendance?.entries?.some((e) => e.approvalStatus === "pending") || false;

  const summary = staffList.reduce(
    (acc, member) => {
      const attendance = attendanceData[member._id];
      const status = getDisplayStatus(attendance);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    { present: 0, pending: 0, absent: 0 },
  );

  return (
    <div className="min-h-screen bg-white text-slate-800">
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md">
              <RiCalendarLine className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800">Attendance</h1>
              <p className="text-[11px] text-blue-400 uppercase">
                Staff Attendance Management
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-blue-50 border border-blue-200 rounded-lg px-1 py-1">
              <button
                onClick={() => setDate((d) => shiftDate(d, -1))}
                className="w-7 h-7 flex items-center justify-center rounded text-blue-500 hover:bg-blue-100"
              >
                <RiArrowLeftSLine className="w-4 h-4" />
              </button>
              <input
                type="date"
                value={date}
                max={todayStr()}
                onChange={(e) => e.target.value && setDate(e.target.value)}
                className="bg-transparent border-none text-sm text-blue-700 font-medium focus:outline-none px-1 cursor-pointer"
              />
              <button
                onClick={() => !isToday && setDate((d) => shiftDate(d, 1))}
                disabled={isToday}
                className="w-7 h-7 flex items-center justify-center rounded text-blue-500 hover:bg-blue-100 disabled:opacity-30"
              >
                <RiArrowRightSLine className="w-4 h-4" />
              </button>
            </div>

            {!isToday && (
              <button
                onClick={() => setDate(todayStr())}
                className="text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100"
              >
                Today
              </button>
            )}

            <button
              onClick={() => loadAll(date)}
              disabled={loading}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </div>
      </header>

      <div className="w-full mx-auto px-6 pt-6 pb-10">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-slate-700">
              {formatDateDisplay(date)}
            </p>
            <p className="text-xs text-blue-400">
              {isToday ? "Today" : "Past date"}
            </p>
          </div>
          <div className="flex gap-3">
            {[
              {
                key: "present",
                color: "text-emerald-700 bg-emerald-50 border-emerald-200",
              },
              {
                key: "pending",
                color: "text-amber-700 bg-amber-50 border-amber-200",
              },
              {
                key: "absent",
                color: "text-red-600 bg-red-50 border-red-200",
              },
            ].map(({ key, color }) => (
              <div
                key={key}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-semibold ${color}`}
              >
                <span className="text-base font-bold">{summary[key] || 0}</span>
                <span className="capitalize">{key}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <RiTeamLine className="w-4 h-4 text-blue-500" />
              Staff Attendance
            </span>
            <span className="text-xs text-blue-400">
              {staffList.length} staff member{staffList.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/30">
                  {[
                    "Photo",
                    "Name",
                    "Position",
                    "Last Check-in Status",
                    "Entries",
                    "Action",
                  ].map((h) => (
                    <th key={h} className="px-4 py-3 text-left">
                      <span className="text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
                        {h}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-400">
                        <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-blue-500" />
                        <span className="text-sm">Loading attendance…</span>
                      </div>
                    </td>
                  </tr>
                ) : staffList.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiTeamLine className="w-7 h-7 text-blue-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          No staff members found.
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  staffList.map((member) => {
                    const attendance = attendanceData[member._id];
                    const displayStatus = getDisplayStatus(attendance);
                    const hasPending = hasPendingEntry(attendance);
                    const entries = attendance?.entries || [];

                    return (
                      <tr
                        key={member._id}
                        className="border-b border-blue-50 hover:bg-blue-50/50 transition"
                      >
                        <td className="px-4 py-3">
                          {member.profilePic ? (
                            <img
                              src={member.profilePic}
                              alt={member.name}
                              className="w-9 h-9 rounded-full object-cover border border-blue-100 shadow-sm"
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold">
                              {(member.name || "?")[0].toUpperCase()}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                          {member.name || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                            {member.position || "Staff"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            if (entries.length === 0) {
                              return (
                                <button
                                  onClick={() => setManualEntryMember(member)}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border bg-red-50 text-red-600 border-red-200 hover:bg-red-100 transition cursor-pointer"
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                  Absent
                                </button>
                              );
                            }

                            // Last entry by createdAt
                            const lastEntry = [...entries].sort(
                              (a, b) =>
                                new Date(b.createdAt) - new Date(a.createdAt),
                            )[0];

                            const approvalColor =
                              lastEntry.approvalStatus === "approved"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : lastEntry.approvalStatus === "pending"
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : "bg-red-50 text-red-600 border-red-200";

                            const approvalDot =
                              lastEntry.approvalStatus === "approved"
                                ? "bg-emerald-500"
                                : lastEntry.approvalStatus === "pending"
                                  ? "bg-amber-400"
                                  : "bg-red-500";

                            const checkStatus =
                              lastEntry.status === "checked-in"
                                ? "Checked In"
                                : "Checked Out";

                            const checkStatusColor =
                              lastEntry.status === "checked-in"
                                ? "text-emerald-600 bg-emerald-50 border-emerald-200"
                                : "text-slate-500 bg-slate-50 border-slate-200";

                            return (
                              <div className="flex flex-col gap-1">
                                {/* Approval status pill — clickable when approved, to revert */}
                                {lastEntry.approvalStatus === "approved" ? (
                                  <button
                                    onClick={() => setRevertMember(member)}
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border w-fit cursor-pointer hover:opacity-80 transition ${approvalColor}`}
                                  >
                                    <span
                                      className={`w-1.5 h-1.5 rounded-full ${approvalDot}`}
                                    />
                                    Approved
                                  </button>
                                ) : (
                                  <span
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border w-fit ${approvalColor}`}
                                  >
                                    <span
                                      className={`w-1.5 h-1.5 rounded-full ${approvalDot}`}
                                    />
                                    {lastEntry.approvalStatus
                                      .charAt(0)
                                      .toUpperCase() +
                                      lastEntry.approvalStatus.slice(1)}
                                  </span>
                                )}
                                {/* Check-in/out status + time — unchanged */}
                                <span
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium border w-fit ${checkStatusColor}`}
                                >
                                  {checkStatus}
                                  {lastEntry.checkInTime && (
                                    <span className="opacity-60">
                                      · {lastEntry.checkInTime}
                                    </span>
                                  )}
                                </span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() =>
                              entries.length > 0 &&
                              setDetailsEntry({ entries, member })
                            }
                            disabled={entries.length === 0}
                            className={`text-xs px-2 py-1 rounded-lg border transition ${
                              entries.length > 0
                                ? "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
                                : "bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed"
                            }`}
                          >
                            {entries.length} entry
                            {entries.length !== 1 ? "s" : ""}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          {hasPending ? (
                            <button
                              onClick={() => handleReviewClick(member)}
                              disabled={loading}
                              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border font-semibold bg-amber-50 border-amber-200 text-amber-600 hover:bg-amber-100 disabled:opacity-50"
                            >
                              {loading ? (
                                <AiOutlineLoading3Quarters className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <RiHourglassLine className="w-3.5 h-3.5" />
                              )}
                              Review Request
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">
                              No action
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {manualEntryMember && (
        <ManualAttendanceModal
          member={manualEntryMember}
          date={date}
          onClose={() => !saving && setManualEntryMember(null)}
          onSubmit={handleManualMark}
          saving={saving}
        />
      )}

      {reviewData && (
        <ReviewModal
          member={reviewData.member}
          attendanceData={reviewData.attendanceData}
          onClose={() => !saving && setReviewData(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          saving={saving}
        />
      )}

      {revertMember && (
        <RevertToAbsentModal
          member={revertMember}
          date={date}
          onClose={() => !saving && setRevertMember(null)}
          onConfirm={handleRevertToAbsent}
          saving={saving}
        />
      )}

      {/* FIX: Pass entries array instead of single entry */}
      {detailsEntry && (
        <EntryDetailsModal
          entries={detailsEntry.entries}
          member={detailsEntry.member}
          onClose={() => setDetailsEntry(null)}
        />
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
const RevertToAbsentModal = ({ member, date, onClose, onConfirm, saving }) => {
  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-md mx-auto bg-white border border-red-100 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-red-100 bg-red-50">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-red-600 flex items-center justify-center text-white">
              <RiCloseCircleLine className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-red-900">
                Revert to Absent
              </h2>
              <p className="text-[11px] text-red-400">
                {member?.name} • {formatDateDisplay(date)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-red-400 hover:text-red-700">
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="flex gap-3">
              <RiErrorWarningLine className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">
                  This cannot be undone
                </p>
                <p className="text-xs text-red-700 mt-1 leading-relaxed">
                  This will permanently delete <strong>all</strong> attendance
                  entries recorded for {member?.name} on this date (check-ins,
                  check-outs, and location data) and mark the day as Absent.
                  Salary will be recalculated automatically.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 rounded-lg text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-100 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold bg-red-600 hover:bg-red-700 text-white shadow-md shadow-red-200 transition disabled:opacity-50"
          >
            {saving ? (
              <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
            ) : (
              <RiCloseCircleLine className="w-4 h-4" />
            )}
            Revert to Absent
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
